import { memo, useCallback, useMemo } from 'react';
import Decimal from 'decimal.js';
import PropTypes from 'prop-types';
import { COMPREHENSIVE_EVALUATION_LABEL } from 'shared-modules/constants';
import { numberExists, roundRoi, roundUpBy1000 } from 'shared-modules/utils';
import { useSelectedStrategyGroup } from 'shared-modules/hooks';
import { useSelectionTermOptionMap, useGetSelectionInstrumentIds } from 'shared-modules/hooks/select';
import { Evaluation } from '../../../../components';
import { ColoredRateText, NumberText } from '../../../../components/Texts';
import styles from './cardItem.module.scss';
import { CardHeader } from '../CardHeader/index';

export const CardItem = memo(({ data, onClick }) => {
  const {
    image,
    name,
    selectionId,
    labId,
    serviceId,
    featureNames,
    simulationStats: {
      selectionStrategyDetailList,
      realizedPl,
      unrealizedPl,
      marginRecommended,
      marginRequired,
      termId,
    },
    defaultSets,
    attribute,
    recommendation,
    strategyList,
  } = data;
  const { isRangeOut, comprehensiveEvaluation } = attribute ?? {};
  const termOptionMap = useSelectionTermOptionMap();
  const [strategyGroup, setStrategyGroup] = useSelectedStrategyGroup();
  const { labId: selectedLabId, selectionId: selectedSelectionId } = strategyGroup ?? {};
  const selected = selectedLabId ? selectedLabId === labId : selectedSelectionId === selectionId;
  const isShare = labId != null;

  const totalPl = useMemo(
    () =>
      numberExists(realizedPl) && numberExists(unrealizedPl)
        ? new Decimal(realizedPl).add(unrealizedPl).mul(defaultSets).toNumber()
        : 0,
    [realizedPl, unrealizedPl, defaultSets],
  );
  const calculatedMarginRecommended = useMemo(() => {
    if (!marginRecommended || !defaultSets) {
      return 0;
    }
    return roundUpBy1000(marginRecommended * defaultSets);
  }, [marginRecommended, defaultSets]);

  const calculatedMarginRequired = useMemo(() => {
    if (!marginRequired) {
      return 0;
    }
    return roundUpBy1000(marginRequired);
  }, [marginRequired]);

  const roi = useMemo(
    () => new Decimal(totalPl).div(calculatedMarginRecommended).mul(100).toNumber(),
    [totalPl, calculatedMarginRecommended],
  );

  const calculatedRoi = useMemo(() => roundRoi(roi), [roi]);

  const instrumentIds = useGetSelectionInstrumentIds({ isShare, strategyList, selectionStrategyDetailList });

  const handleClickItem = useCallback(() => {
    if (onClick) {
      onClick();
    }
    if (!selected) {
      setStrategyGroup(data);
    }
  }, [selected, data, setStrategyGroup, onClick]);

  return (
    <div className={styles.container} onClick={handleClickItem} role="button" aria-hidden>
      {selected && (
        <div className={styles.lineContainer}>
          <div className={styles.line} />
        </div>
      )}
      <div className={styles.content}>
        <div className={styles.details}>
          <CardHeader
            image={image}
            name={name}
            serviceId={serviceId}
            featureNames={featureNames}
            instrumentIds={instrumentIds}
            isRangeOut={isRangeOut}
            recommendation={recommendation}
            isShare={isShare}
          />
          <div className={styles.stats}>
            <div className={styles.item}>
              <div className={styles.label}>{COMPREHENSIVE_EVALUATION_LABEL}</div>
              <Evaluation
                value={comprehensiveEvaluation}
                iconClassName={styles.iconClass}
                scoreClassName={styles.evaluationScoreClass}
              />
            </div>
            <div className={styles.item}>
              <div className={styles.label}>{`収益率(${termOptionMap.get(String(termId))})`}</div>
              <ColoredRateText className={styles.value} value={calculatedRoi} symbolClassName={styles.percent} />
            </div>
            <div className={styles.item}>
              <div className={styles.label}>発注証拠金目安</div>
              <NumberText
                value={calculatedMarginRequired}
                className={styles.value}
                symbolClassName={styles.symbol}
                symbol="円"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

CardItem.propTypes = {
  data: PropTypes.shape({
    selectionId: PropTypes.number,
    labId: PropTypes.number,
    image: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]).isRequired,
    serviceId: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    style: PropTypes.string,
    period: PropTypes.string,
    featureNames: PropTypes.arrayOf(PropTypes.string),
    technicalTerm: PropTypes.string,
    selectionVersion: PropTypes.number,
    simulationStats: PropTypes.shape({
      roi: PropTypes.number,
      marginRecommended: PropTypes.number,
      marginRequired: PropTypes.number,
      selectionStrategyDetailList: PropTypes.arrayOf(PropTypes.shape({})),
      maxDd: PropTypes.number,
      realizedPl: PropTypes.number,
      unrealizedPl: PropTypes.number,
      termId: PropTypes.number,
    }).isRequired,
    defaultSets: PropTypes.number,
    attribute: PropTypes.shape({
      isRangeOut: PropTypes.bool,
      comprehensiveEvaluation: PropTypes.number,
    }),
    recommendation: PropTypes.bool,
    strategyList: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  onClick: PropTypes.func,
};

CardItem.defaultProps = {
  onClick: undefined,
};
